import { useMemo, useRef, useState } from 'react';
import { Button, Modal, useModalRequestClose } from '@safetyhub/ui';
import { LEGAL_POSITIONS, LEGAL_POSITION_ORDER, PERMISSION_GROUP_LABELS, groupPermissions } from '@safetyhub/shared';
import type {
  AdminCompanyRoleView,
  AdminCreateCompanyRoleRequest,
  AdminUpdateCompanyRoleRequest,
  LegalPositionKey,
  PermissionDefinitionView,
} from '../../lib/company-rbac';

export type CompanyRoleFormMode = { kind: 'create' } | { kind: 'edit'; role: AdminCompanyRoleView };

interface FormValues {
  key: string;
  name: string;
  description: string;
  legalPosition: LegalPositionKey | '';
  singleHolder: boolean;
  allowsNoAccount: boolean;
  sortOrder: number;
  permissionKeys: Set<string>;
}

/** 시스템 역할 key 규칙 — 서버 DTO `SYSTEM_ROLE_KEY_PATTERN`과 같은 식(입력 중 안내용) */
const KEY_PATTERN = /^(?!co-)[a-z][a-z0-9-]*$/;

/** 조직 소유자가 조정할 수 없는 키(`orgAdjustable=false`) 표식 */
export const LOCKED_HINT = '조직 조정 불가 — 조직 오버라이드에서 이 키는 시스템 값으로 고정됩니다.';

function toFormValues(mode: CompanyRoleFormMode): FormValues {
  if (mode.kind === 'create') {
    return {
      key: '',
      name: '',
      description: '',
      legalPosition: '',
      singleHolder: false,
      allowsNoAccount: false,
      sortOrder: 100,
      permissionKeys: new Set(),
    };
  }
  const r = mode.role;
  return {
    key: r.key,
    name: r.name,
    description: r.description ?? '',
    legalPosition: r.legalPosition ?? '',
    singleHolder: r.singleHolder,
    allowsNoAccount: r.allowsNoAccount,
    sortOrder: r.sortOrder,
    permissionKeys: new Set(r.permissionKeys),
  };
}

function sameSet(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const k of a) if (!b.has(k)) return false;
  return true;
}

/** 수정 모드에서 서버로 보낼 diff — 바뀐 필드만(권한은 전체 집합 교체) */
export function buildUpdateBody(initial: FormValues, values: FormValues, isOwner: boolean): AdminUpdateCompanyRoleRequest {
  const body: AdminUpdateCompanyRoleRequest = {};
  if (values.name.trim() !== initial.name) body.name = values.name.trim();
  if (values.description.trim() !== initial.description) body.description = values.description.trim() || null;
  if (values.legalPosition !== initial.legalPosition) body.legalPosition = values.legalPosition || null;
  if (values.singleHolder !== initial.singleHolder) body.singleHolder = values.singleHolder;
  if (values.allowsNoAccount !== initial.allowsNoAccount) body.allowsNoAccount = values.allowsNoAccount;
  if (values.sortOrder !== initial.sortOrder) body.sortOrder = values.sortOrder;
  // owner는 전체 키 고정 — 서버가 400으로 거부하므로 애초에 싣지 않는다(화면도 체크를 잠근다)
  if (!isOwner && !sameSet(values.permissionKeys, initial.permissionKeys)) {
    body.permissionKeys = Array.from(values.permissionKeys).sort();
  }
  return body;
}

interface CompanyRoleFormModalProps {
  mode: CompanyRoleFormMode;
  permissions: PermissionDefinitionView[];
  busy: boolean;
  /** 서버 에러 문구(호출부가 토스트와 별개로 폼 안에 보여 줄 때) */
  errorMessage?: string | null;
  onCreate: (body: AdminCreateCompanyRoleRequest) => void;
  onUpdate: (key: string, body: AdminUpdateCompanyRoleRequest) => void;
  onClose: () => void;
}

/**
 * 시스템 역할 생성/수정 모달 — 속성(이름·설명·법정 직위·성질·정렬) + 권한 매트릭스(22키 group별 체크).
 *
 * dirty 표준(결정 `2026-08-16-edit-modal-dirty-gate-standard`): 수정 모드는 열림 시점 스냅샷 대조로 `isDirty`를 계산해
 * 저장 버튼 `!isDirty` 게이트 + 「변경사항 없음/있습니다」 + base `Modal isDirty`(Esc·오버레이·✕·취소가 확인 모달을 거친다).
 * 생성 모드도 빈 폼 대비 스냅샷으로 base `Modal isDirty`를 건다 — 저장 버튼 게이트·「변경사항」 문구만 미적용.
 * 대상이 바뀌면 호출부가 `key`로 remount시킨다.
 *
 * owner 역할은 권한 전부 체크 + 잠금(판정이 전체 키를 주므로 편집이 무의미 — 서버도 400). `orgAdjustable=false` 키는
 * 🔒 「조직 조정 불가」 표식(admin은 여전히 켜고 끌 수 있다 — 잠금은 조직 상한이지 admin 제약이 아니다).
 */
export default function CompanyRoleFormModal({
  mode,
  permissions,
  busy,
  errorMessage,
  onCreate,
  onUpdate,
  onClose,
}: CompanyRoleFormModalProps) {
  // 열림 시점의 값이 dirty 기준선 — lazy 초기화(매 렌더 인자 평가 방지)
  const [initial] = useState<FormValues>(() => toFormValues(mode));
  const [values, setValues] = useState<FormValues>(initial);
  const [sortOrderText, setSortOrderText] = useState<string>(() => String(initial.sortOrder));
  const nameRef = useRef<HTMLInputElement>(null);

  const isEdit = mode.kind === 'edit';
  const isOwner = mode.kind === 'edit' && mode.role.isOwner;
  const groups = useMemo(() => groupPermissions(permissions), [permissions]);

  const trimmedName = values.name.trim();
  const keyValid = !isEdit ? KEY_PATTERN.test(values.key) : true;

  // 생성 모드도 같은 스냅샷 대조다(기준선 = 빈 폼) — key는 수정 diff 밖이라 따로 본다. 설명·법정 직위·성질·정렬만
  // 바꿔도 「적던 내용」이어서, 입력 존재(이름·key·권한)만 세면 그 넷을 바꾼 폼이 확인 없이 닫힌다.
  const isDirty =
    (!isEdit && values.key !== initial.key) || Object.keys(buildUpdateBody(initial, values, isOwner)).length > 0;
  const canSubmit = trimmedName.length > 0 && keyValid && !busy && (!isEdit || isDirty);

  const togglePermission = (key: string) => {
    if (isOwner) return;
    setValues((prev) => {
      const next = new Set(prev.permissionKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...prev, permissionKeys: next };
    });
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (mode.kind === 'edit') {
      onUpdate(mode.role.key, buildUpdateBody(initial, values, isOwner));
      return;
    }
    onCreate({
      key: values.key,
      name: trimmedName,
      description: values.description.trim() || undefined,
      legalPosition: values.legalPosition || null,
      singleHolder: values.singleHolder,
      allowsNoAccount: values.allowsNoAccount,
      sortOrder: values.sortOrder,
      permissionKeys: Array.from(values.permissionKeys).sort(),
    });
  };

  const inputClass =
    'w-full rounded-control border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50 disabled:text-gray-500';

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `역할 수정 — ${mode.role.name}` : '시스템 역할 추가'}
      size="xl"
      isDirty={isDirty}
      initialFocusRef={nameRef}
      footer={
        <FormFooter isEdit={isEdit} isDirty={isDirty} canSubmit={canSubmit} busy={busy} onSubmit={handleSubmit} onClose={onClose} />
      }
    >
      <div className="space-y-5" data-testid="company-role-form">
        {errorMessage && (
          <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm" role="alert">
            {errorMessage}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">key</span>
            <input
              type="text"
              className={`${inputClass} font-mono`}
              value={values.key}
              disabled={isEdit}
              maxLength={50}
              placeholder="site-clerk"
              onChange={(e) =>
                setValues((prev) => ({
                  ...prev,
                  key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-'),
                }))
              }
            />
            <span className="mt-1 block text-xs text-gray-400">
              {isEdit ? '만든 뒤에는 바꿀 수 없습니다(배정·오버라이드가 참조).' : '영문 소문자·숫자·하이픈, co- 접두 불가.'}
            </span>
            {!isEdit && values.key.length > 0 && !keyValid && (
              <span className="mt-1 block text-xs text-red-600">key 형식이 맞지 않습니다.</span>
            )}
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">이름</span>
            <input
              ref={nameRef}
              type="text"
              className={inputClass}
              value={values.name}
              maxLength={50}
              placeholder="예: 현장 서무"
              onChange={(e) => setValues((prev) => ({ ...prev, name: e.target.value }))}
            />
          </label>

          <label className="block md:col-span-2">
            <span className="mb-1 block text-sm font-medium text-gray-700">설명</span>
            <input
              type="text"
              className={inputClass}
              value={values.description}
              maxLength={500}
              onChange={(e) => setValues((prev) => ({ ...prev, description: e.target.value }))}
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">법정 직위</span>
            <select
              className={inputClass}
              value={values.legalPosition}
              onChange={(e) => setValues((prev) => ({ ...prev, legalPosition: e.target.value as LegalPositionKey | '' }))}
            >
              <option value="">해당 없음</option>
              {LEGAL_POSITION_ORDER.map((k) => (
                <option key={k} value={k}>
                  {LEGAL_POSITIONS[k].label} · {LEGAL_POSITIONS[k].article}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">정렬 순서</span>
            <input
              type="number"
              min={0}
              step={1}
              className={inputClass}
              value={sortOrderText}
              onChange={(e) => {
                const raw = e.target.value;
                setSortOrderText(raw);
                const n = Number.parseInt(raw, 10);
                setValues((prev) => ({ ...prev, sortOrder: Number.isFinite(n) && n >= 0 ? n : 0 }));
              }}
              onBlur={() => setSortOrderText(String(values.sortOrder))}
            />
          </label>

          <div className="flex flex-wrap gap-6 md:col-span-2">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-brand-600"
                checked={values.singleHolder}
                onChange={(e) => setValues((prev) => ({ ...prev, singleHolder: e.target.checked }))}
              />
              한 조직에 1인만(singleHolder)
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-brand-600"
                checked={values.allowsNoAccount}
                onChange={(e) => setValues((prev) => ({ ...prev, allowsNoAccount: e.target.checked }))}
              />
              계정 없는 인원에도 배정 가능(allowsNoAccount)
            </label>
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-baseline justify-between">
            <h4 className="text-sm font-semibold text-gray-900">권한</h4>
            <span className="text-xs text-gray-500">
              {isOwner ? '소유자 역할은 전체 권한으로 고정됩니다.' : `${values.permissionKeys.size} / ${permissions.length} 선택`}
            </span>
          </div>
          <p className="mb-3 text-xs text-gray-500">
            시스템 역할의 권한 변경은 조직이 이미 만든 오버라이드에는 전파되지 않습니다. 단 🔒 표시된(조직 조정 불가) 키는
            판정에서 시스템 값이 이기므로 오버라이드한 조직에도 즉시 적용됩니다.
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="permission-matrix">
            {groups.map(({ group, items }) => (
              <fieldset key={group} className="rounded-card border border-gray-200 p-3">
                <legend className="px-1 text-xs font-semibold text-gray-500">{PERMISSION_GROUP_LABELS[group] ?? group}</legend>
                <div className="space-y-1.5">
                  {items.map((p) => {
                    const checked = isOwner || values.permissionKeys.has(p.key);
                    return (
                      <label
                        key={p.key}
                        className="flex items-start gap-2 text-sm text-gray-800"
                        data-permission-key={p.key}
                        data-locked={p.orgAdjustable ? undefined : 'true'}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 disabled:opacity-50"
                          checked={checked}
                          disabled={isOwner || busy}
                          onChange={() => togglePermission(p.key)}
                          aria-label={p.label}
                        />
                        <span className="flex-1">
                          <span className="inline-flex items-center gap-1">
                            {p.label}
                            {!p.orgAdjustable && (
                              <span title={LOCKED_HINT} aria-label="조직 조정 불가" className="text-xs">
                                🔒
                              </span>
                            )}
                            <span className="font-mono text-[11px] text-gray-400">{p.key}</span>
                          </span>
                          {p.description && <span className="block text-[11px] text-gray-400">{p.description}</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/**
 * 푸터는 별도 컴포넌트여야 한다 — `useModalRequestClose`는 Modal children **하위**에서만 값을 잡는다
 * (부모 본문에서 부르면 null이라 dirty 확인이 우회된다).
 */
function FormFooter({
  isEdit,
  isDirty,
  canSubmit,
  busy,
  onSubmit,
  onClose,
}: {
  isEdit: boolean;
  isDirty: boolean;
  canSubmit: boolean;
  busy: boolean;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const requestClose = useModalRequestClose();
  return (
    <div className="flex w-full items-center gap-3">
      {isEdit && (
        <span className="text-xs text-gray-500" data-testid="dirty-hint">
          {isDirty ? '변경사항이 있습니다' : '변경사항 없음'}
        </span>
      )}
      <div className="ml-auto flex gap-2">
        <Button variant="secondary" size="md" onClick={() => (requestClose ?? onClose)()} disabled={busy}>
          취소
        </Button>
        <Button variant="primary" size="md" disabled={!canSubmit} onClick={onSubmit}>
          {busy ? '저장 중…' : isEdit ? '저장' : '추가'}
        </Button>
      </div>
    </div>
  );
}
